<template>
  <div class="tenant-setting-edit-page">
    <div class="page-header">
      <div>
        <p class="eyebrow">TENANT SETTINGS</p>
        <h1>自社設定を編集</h1>
        <p>この画面の設定は、現在ログインしている会社のデータと通知だけに適用されます。</p>
      </div>
    </div>

    <CAlert v-if="errorMessage" color="danger" close-button @update:show="errorMessage = ''">
      {{ errorMessage }}
    </CAlert>

    <div v-if="loadingInitial" class="setting-loading">
      <CSpinner color="primary"/>
      <span>自社設定を読み込んでいます。</span>
    </div>

    <ValidationObserver v-else ref="observer">
      <form @submit.prevent="openModal">
        <CCard class="setting-form-card">
          <CCardHeader>
            <div class="section-heading">
              <span class="section-number">1</span>
              <div><strong>会社情報</strong><p>会社名、連絡先として使用する情報です。</p></div>
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol sm="12" md="6">
                <ValidationProvider name="会社名" rules="required|max:100" v-slot="provider">
                  <label>会社名<span class="asterisk">*</span></label>
                  <CInput v-model.trim="form.company_name"/>
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </CCol>
              <CCol sm="12" md="6">
                <ValidationProvider name="会社メール" rules="email|max:254" v-slot="provider">
                  <label>会社メール</label>
                  <CInput type="email" v-model.trim="form.company_email"/>
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </CCol>
              <CCol sm="12" md="6">
                <ValidationProvider name="電話番号" rules="max:16" v-slot="provider">
                  <label>電話番号</label>
                  <CInput v-model.trim="form.telephone" description="任意項目です。既存のハイフン付き番号もそのまま保存できます。"/>
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </CCol>
              <!--
                ダッシュボードに出す社内カレンダー。

                **空なら出さない。**既定値を持たせない。2026-08-27 まで
                販売元のカレンダーが直に埋め込まれていて、どの会社の
                ダッシュボードにも出ていた。
              -->
              <CCol sm="12">
                <ValidationProvider name="社内カレンダー" rules="max:1000" v-slot="provider">
                  <label>社内カレンダー（Google カレンダーの埋め込みURL）</label>
                  <CInput
                    v-model.trim="form.dashboard_calendar_embed_url"
                    placeholder="https://calendar.google.com/calendar/embed?src=..."
                    description="Google カレンダーの「設定と共有」→「カレンダーの統合」にある埋め込みコードの src= に入っているURLを貼り付けてください。空欄の場合、ダッシュボードにカレンダーは表示されません。"
                  />
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </CCol>
              <!--
                提出物の締め。会社ごと。

                **空なら期限を出さない。**既定値を持たせない。2026-08-27
                まで「第2営業日まで」と画面に直に書かれていて、これは
                販売元の締めだった。他社の社員は毎日、自社と違う期限を
                見せられていた。
              -->
              <CCol sm="4">
                <label>提出物の締め（数え方）</label>
                <select v-model="form.submission_due_kind" class="form-control">
                  <option value="">決めない（期限を出さない）</option>
                  <option value="BUSINESS_DAY">翌月の第N営業日まで</option>
                  <option value="CALENDAR_DAY">翌月のN日まで</option>
                </select>
              </CCol>
              <CCol sm="2">
                <ValidationProvider name="締めの日" rules="numeric|min_value:1|max_value:31" v-slot="provider">
                  <label>N</label>
                  <CInput v-model.trim="form.submission_due_day" placeholder="2"/>
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </CCol>
              <CCol sm="6">
                <ValidationProvider name="提出物の内容" rules="max:60" v-slot="provider">
                  <label>そのとき出すもの</label>
                  <CInput
                    v-model.trim="form.submission_due_items"
                    placeholder="勤務表・業務報告・通勤費・経費"
                    description="ダッシュボードの提出ガイドで、期限の下に表示されます。使用しないものは空欄にしてください。"
                  />
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <CCard class="setting-form-card">
          <CCardHeader>
            <div class="section-heading">
              <span class="section-number">2</span>
              <div><strong>勤怠計算・打刻</strong><p>勤務表と打刻の会社共通計算ルールです。</p></div>
            </div>
          </CCardHeader>
          <CCardBody>
            <CAlert color="info">
              未設定時は、打刻猶予なし・時間外1分単位・規定勤務8時間・規定休憩1時間を使用します。
              設定した切り上げ単位は時間外を従業員有利に切り上げるために使い、実際の労働時間を切り捨てる用途には使用しません。
            </CAlert>
            <CRow>
              <CCol sm="12" md="6" lg="4" v-for="field in timeFields" :key="field.key">
                <ValidationProvider :name="field.label" rules="required|hourMinute" v-slot="provider">
                  <label>{{ field.label }}<span class="asterisk">*</span></label>
                  <CInput v-model="form[field.key]" placeholder="00:00"/>
                  <small>{{ field.description }}</small>
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </CCol>
            </CRow>

            <div class="switch-grid">
              <label class="setting-switch-card" for="postWorkBreakFlg">
                <input id="postWorkBreakFlg" type="checkbox" v-model="form.post_work_break_flg"/>
                <span><strong>定時後の猶予時間を休憩として扱う</strong><small>設定した定時後打刻猶予時間を、時間外勤務時の追加休憩として勤務時間から除外します。</small></span>
              </label>
              <label class="setting-switch-card" for="projectBreakFlg">
                <input id="projectBreakFlg" type="checkbox" v-model="form.use_project_break_flg"/>
                <span><strong>プロジェクト休憩を自動反映する</strong><small>プロジェクトに設定した休憩時間を勤務表へ反映します。</small></span>
              </label>
            </div>
          </CCardBody>
        </CCard>

        <CCard class="setting-form-card">
          <CCardHeader>
            <div class="section-heading">
              <span class="section-number">3</span>
              <div>
                <strong>就業時間</strong>
                <p>朝と夕方のお知らせは、ここで決めた時間を基準に届きます。</p>
              </div>
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol sm="12" md="6" lg="4">
                <ValidationProvider name="始業時刻" rules="required|hourMinute" v-slot="provider">
                  <label>始業時刻<span class="asterisk">*</span></label>
                  <CInput v-model="form.business_start_time" placeholder="09:00"/>
                  <small>未設定は09:00です。</small>
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </CCol>
              <CCol sm="12" md="6" lg="4">
                <ValidationProvider name="終業時刻" rules="required|hourMinute" v-slot="provider">
                  <label>終業時刻<span class="asterisk">*</span></label>
                  <CInput v-model="form.business_end_time" placeholder="18:00"/>
                  <small>未設定は18:00です。</small>
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </CCol>
            </CRow>

            <CRow>
              <CCol sm="12" md="6" lg="4" v-for="field in leadFields" :key="field.key">
                <label>{{ field.label }}</label>
                <CInput type="number" min="0" max="720" v-model.number="form[field.key]"/>
                <small>{{ field.description }}</small>
              </CCol>
            </CRow>

            <CAlert color="info">
              既定のまま（始業09:00・終業18:00・30分／0分／30分）にしておくと、
              これまでどおり 8:30・9:00・17:30 に届きます。
              実際に届く時刻は、保存したあとの自社設定画面で確認できます。
              前倒しを長くしすぎて前の日に回り込む組み合わせは保存できません。
            </CAlert>
          </CCardBody>
        </CCard>

        <CCard class="setting-form-card">
          <CCardHeader>
            <div class="section-heading">
              <span class="section-number">4</span>
              <div><strong>Slack連携・通知</strong><p>通知種類ごとにオン・オフと通知先を設定します。</p></div>
            </div>
          </CCardHeader>
          <CCardBody>
            <CAlert v-if="!form.slack_feature_enabled" color="warning">
              現在のご契約や設定ではSlack通知を利用できないため、通知設定は変更できません。
            </CAlert>

            <div class="slack-credential-card" :class="{ 'is-disabled': !form.slack_feature_enabled }">
              <div class="credential-heading">
                <div>
                  <h3>Slack接続情報</h3>
                  <p>Botトークンはセキュリティ保護のため画面に表示されません。変更する場合のみ新しい値を入力してください。</p>
                </div>
                <CBadge :color="form.slack_token_configured ? 'success' : 'secondary'">
                  {{ form.slack_token_configured ? 'トークン設定済み' : 'トークン未設定' }}
                </CBadge>
              </div>

              <ValidationProvider name="Slack Botトークン" rules="max:100" v-slot="provider">
                <label>
                  Slack Botトークン
                  <span v-if="anySlackEnabled && !form.slack_token_configured" class="asterisk">*</span>
                </label>
                <CInput
                  type="password"
                  v-model.trim="form.slack_token"
                  :disabled="!form.slack_feature_enabled || form.clear_slack_token"
                  :placeholder="form.slack_token_configured ? '変更する場合だけ入力' : 'xoxb- から始まるBotトークン'"
                  autocomplete="new-password"
                />
                <p class="error">{{ provider.errors[0] }}</p>
              </ValidationProvider>

              <label v-if="form.slack_token_configured" class="clear-token-option">
                <input
                  type="checkbox"
                  v-model="form.clear_slack_token"
                  :disabled="!form.slack_feature_enabled"
                />
                <span><strong>保存済みトークンを削除する</strong><small>通知をすべてオフにした場合だけ削除できます。</small></span>
              </label>
            </div>

            <div class="notification-setting-grid">
              <article class="notification-setting-card" :class="{ 'is-disabled': !form.slack_feature_enabled }">
                <div class="notification-toggle-row">
                  <div><h3>申請状況の即時通知</h3><p>申請、取消、差戻しが発生した時点で送信します。</p></div>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="form.slack_notification_flg" :disabled="!form.slack_feature_enabled"/>
                    <span aria-hidden="true"></span>
                    <strong>{{ form.slack_notification_flg ? 'オン' : 'オフ' }}</strong>
                  </label>
                </div>
                <ValidationProvider
                  name="即時通知先チャンネルID"
                  :rules="form.slack_notification_flg ? 'required|max:50' : 'max:50'"
                  v-slot="provider"
                >
                  <label>通知先チャンネルID<span v-if="form.slack_notification_flg" class="asterisk">*</span></label>
                  <CInput
                    v-model.trim="form.slack_channel"
                    :disabled="!form.slack_feature_enabled || !form.slack_notification_flg"
                    placeholder="例：C0123456789"
                  />
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </article>

              <article class="notification-setting-card" :class="{ 'is-disabled': !form.slack_feature_enabled }">
                <div class="notification-toggle-row">
                  <div><h3>承認待ちリマインド</h3><p>会社営業日に1日1回だけ送信します。時刻は就業時間の設定で決まります。</p></div>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="form.pending_approval_notification_flg" :disabled="!form.slack_feature_enabled"/>
                    <span aria-hidden="true"></span>
                    <strong>{{ form.pending_approval_notification_flg ? 'オン' : 'オフ' }}</strong>
                  </label>
                </div>
                <ValidationProvider
                  name="承認待ち通知先チャンネルID"
                  :rules="form.pending_approval_notification_flg ? 'required|max:50' : 'max:50'"
                  v-slot="provider"
                >
                  <label>通知先チャンネルID<span v-if="form.pending_approval_notification_flg" class="asterisk">*</span></label>
                  <CInput
                    v-model.trim="form.pending_approval_slack_channel"
                    :disabled="!form.slack_feature_enabled || !form.pending_approval_notification_flg"
                    placeholder="例：C0123456789"
                  />
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
                <div class="notification-schedule">
                  <strong>実行時間</strong>
                  <span>会社営業日に1日1回（時刻は就業時間の設定で決まります）</span>
                  <small>送信結果と失敗理由は、自社設定の稼働確認で確認できます。</small>
                </div>
              </article>

              <article class="notification-setting-card" :class="{ 'is-disabled': !form.slack_feature_enabled }">
                <div class="notification-toggle-row">
                  <div><h3>管理部門向け朝通知</h3><p>当日の承認済み休暇予定だけを送信します。時刻は就業時間の設定で決まります。</p></div>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="form.management_daily_notification_flg" :disabled="!form.slack_feature_enabled"/>
                    <span aria-hidden="true"></span>
                    <strong>{{ form.management_daily_notification_flg ? 'オン' : 'オフ' }}</strong>
                  </label>
                </div>
                <ValidationProvider
                  name="管理部門向け朝通知先チャンネルID"
                  :rules="form.management_daily_notification_flg ? 'required|max:50' : 'max:50'"
                  v-slot="provider"
                >
                  <label>通知先チャンネルID<span v-if="form.management_daily_notification_flg" class="asterisk">*</span></label>
                  <CInput
                    v-model.trim="form.management_daily_slack_channel"
                    :disabled="!form.slack_feature_enabled || !form.management_daily_notification_flg"
                    placeholder="例：C0123456789"
                  />
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </article>

              <!--
                36協定アラート。**月に1回だけ。**

                これは会社が受け取るかどうかを選べるたぐいの知らせではない
                ので、**既定でオン**にしてある。上限を超えれば違反で、
                超えそうなら手を打つしかない。

                通知先は2段。専用のチャンネルが空なら、管理部門向けの
                チャンネルへ送る。文面には氏名も部署名も書かず、人数の内訳と
                画面への案内だけなので、あちらに既に流れている氏名つきの
                休暇予定より扱いは軽い。**だから通知先は必須にしない。**
                必須にすると、既定で立っているフラグのせいで、この画面そのものが
                保存できなくなる。

                **ただし、管理部門向け朝通知を止めていれば倒さない。**
                止めてもチャンネルIDの値はこの画面に残る（入力を灰色にする
                だけで消さない）ので、残っている＝送ってよい、にすると、
                自分で止めたチャンネルへ頼んでいない通知が届き始める。

                そのかわり**届かないときは必ず帯を出す。**黙って何も
                起きない状態を作らない。

                算定期日は労働安全衛生規則52条の2第2項の「毎月一回以上、
                一定の期日」。既定は翌月10日。
              -->
              <!--
                **判定を出していないあいだは、この知らせの設定も出さない。**
                通知は1通も送らないので、通知先を決めても何も起きない。
                直しても何も起きない設定を促すと、次に本当に届かなく
                なったときにはもう読まれない。
              -->
              <article v-if="legalComplianceEnabled" class="notification-setting-card" :class="{ 'is-disabled': !form.slack_feature_enabled }">
                <div class="notification-toggle-row">
                  <div>
                    <h3>36協定アラート</h3>
                    <p>算定期日に、上限に触れている方の人数を送信します。氏名は送らず、画面で確認します。</p>
                  </div>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="form.overtime_alert_notification_flg" :disabled="!form.slack_feature_enabled"/>
                    <span aria-hidden="true"></span>
                    <strong>{{ form.overtime_alert_notification_flg ? 'オン' : 'オフ' }}</strong>
                  </label>
                </div>

                <div
                  v-if="overtimeAlertNotice"
                  class="delivery-notice"
                  :class="'delivery-notice--' + overtimeAlertNotice.tone"
                  role="status"
                >
                  <strong>{{ overtimeAlertNotice.headline }}</strong>
                  <small>{{ overtimeAlertNotice.detail }}</small>
                </div>

                <ValidationProvider
                  name="36協定アラートの通知先チャンネルID"
                  rules="max:50"
                  v-slot="provider"
                >
                  <label>通知先チャンネルID</label>
                  <CInput
                    v-model.trim="form.overtime_alert_slack_channel"
                    :disabled="!form.slack_feature_enabled || !form.overtime_alert_notification_flg"
                    placeholder="例：C0123456789"
                  />
                  <p class="field-hint">{{ overtimeAlertChannelHint }}</p>
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>

                <!--
                  **法令の合図はどれも事後。**いちばん早い「限度時間を
                  超えた」でも45時間を超えたあとで、しかも通知は月1回。
                  月半ばに超えた人は3週間後に知らされる。

                  ここは**会社が決める社内の目安**で、法令ではない。
                  空なら予告しない。
                -->
                <ValidationProvider
                  name="上限に近づいたら知らせる割合"
                  rules="numeric|min_value:1|max_value:99"
                  v-slot="provider"
                >
                  <label>上限に近づいたら知らせる割合（%）</label>
                  <CInput
                    v-model.trim="form.overtime_alert_threshold_percent"
                    type="number"
                    min="1"
                    max="99"
                    :disabled="!form.slack_feature_enabled"
                    placeholder="例：80"
                  />
                  <p class="field-hint">{{ overtimeAlertThresholdHint }}</p>
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
                <CRow>
                  <CCol sm="7">
                    <label>算定期日（数え方）</label>
                    <select
                      v-model="form.overtime_calculation_due_kind"
                      class="form-control"
                      :disabled="!form.slack_feature_enabled"
                    >
                      <option value="">設定しない（お知らせを止める）</option>
                      <option value="BUSINESS_DAY">翌月の第N営業日</option>
                      <option value="CALENDAR_DAY">翌月のN日（既定）</option>
                    </select>
                  </CCol>
                  <CCol sm="5">
                    <ValidationProvider name="算定期日の日" rules="numeric|min_value:1|max_value:31" v-slot="provider">
                      <label>N</label>
                      <CInput
                        v-model.trim="form.overtime_calculation_due_day"
                        :disabled="!form.slack_feature_enabled"
                        placeholder="10"
                      />
                      <p class="error">{{ provider.errors[0] }}</p>
                    </ValidationProvider>
                  </CCol>
                </CRow>
                <p class="field-hint">
                  既定は「翌月10日」です。勤務表の承認が終わる時期に合わせて変えられます。
                  早すぎると、まだ判定できない方ばかりのお知らせが届きます。
                </p>
                <div class="notification-schedule">
                  <strong>実行時間</strong>
                  <span>算定期日（会社休日なら次の営業日）の始業時刻に、月1回</span>
                  <small>労働安全衛生規則52条の2第2項の「毎月一回以上、一定の期日」です。該当者がいない月は送りません。</small>
                </div>
              </article>
            </div>
          </CCardBody>
        </CCard>

        <div class="form-actions">
          <CButton type="button" color="secondary" variant="outline" @click="backToSetting">戻る</CButton>
          <CButton type="submit" color="primary" :disabled="saving">確認</CButton>
        </div>
      </form>
    </ValidationObserver>

    <!--
      36協定（時間外労働の上限）。**上の自社設定とは別に保存する。**

      同じ保存に載せていたころ、この画面はフォームの全項目をまとめて
      送っていた。会社名を1文字直して保存しただけで協定の全項目が一緒に飛び、
      適用開始日が空だと「いま効いている協定を書き換える」ほうへ入る。
      特別条項の有無ひとつで年の天井が360時間から720時間へ変わるのに、
      **書き換わったことは画面のどこにも出なかった。**

      だから欄も、保存ボタンも、送り先も分けてある。操作も2つに分けた
      ——**新しい協定を作る**か、**この協定を編集する**か。「打った日が
      既存と一致したら書き換え」は使わない。打ち間違いで去年の協定が
      書き換わり、入れた人には追加したように見える。
    -->
    <div v-if="!loadingInitial && legalComplianceEnabled" class="separate-save-divider">
      <span>ここから下は、上の自社設定とは別に保存します</span>
    </div>

    <!--
      **判定を出していないあいだは、協定そのものの入力欄も出さない。**

      上の区切り帯だけを囲っていたので、帯は消えるのに
      「時間外労働の上限（36協定）」のカードは残っていた。
      書く口は back が 403 で断るので保存はできないが、
      <b>入れられる画面が出ているのに保存できないのは、いちばん悪い。</b>
    -->
    <ValidationObserver v-if="!loadingInitial && legalComplianceEnabled" ref="agreementObserver">
      <form class="agreement-form" @submit.prevent="saveAgreement">
        <CCard class="setting-form-card">
          <CCardHeader>
            <div class="section-heading">
              <span class="section-number section-number--agreement">36</span>
              <div>
                <strong>時間外労働の上限（36協定）</strong>
                <p>
                  労働基準法36条の上限です。自社の36協定に書いた内容を入れてください。
                  <strong>この欄は、上の自社設定とは別に保存します。</strong>
                </p>
              </div>
            </div>
          </CCardHeader>
          <CCardBody>
            <!--
              いま判定できているのか。**「問題なし」と同じ顔にしない。**
              判定できていないことが見えないと、上限を超えた人がいても
              何も起きないまま月が過ぎる。
            -->
            <div
              class="agreement-status"
              :class="agreementStatusClasses"
              role="status"
            >
              <span class="agreement-status__badge">{{ agreementStatusNotice.badge }}</span>
              <div class="agreement-status__body">
                <strong>{{ agreementStatusNotice.headline }}</strong>
                <small>{{ agreementStatusNotice.detail }}</small>
                <small class="agreement-status__action">{{ agreementStatusNotice.action }}</small>
              </div>
            </div>

            <!--
              ここに出すのは、履歴の［編集］で断った理由だけ。［保存］の結果は
              ［保存］のすぐ上に出す（ここから［保存］まで約340行あり、見えなかった）。
            -->
            <CAlert v-if="agreementError && !agreementErrorNearSave" color="danger" class="agreement-error">
              <span>{{ agreementError }}</span>
            </CAlert>

            <!--
              登録されている協定。**表示するだけにしない。**
              読み込む導線が無かったころ、会社が触れるのは「いま効いている
              1本」だけで、去年の協定を直す道が画面に無かった。
            -->
            <div v-if="agreementHistory.length" class="agreement-history">
              <h6>登録されている協定（新しい順）</h6>
              <table>
                <thead>
                  <tr>
                    <th>適用開始日</th>
                    <th>有効期間の終わり</th>
                    <th>月の限度時間</th>
                    <th>年の限度時間</th>
                    <th>特別条項</th>
                    <th>内容</th>
                    <th class="agreement-history__action-head">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, index) in agreementHistory"
                    :key="agreementRowKey(row, index)"
                    :class="{ 'is-editing': isEditingRow(row) }"
                  >
                    <td>
                      {{ dayLabel(row.valid_from) }}
                      <CBadge v-if="row.current_flg" color="info">いま効いている</CBadge>
                    </td>
                    <td>{{ dayLabel(row.valid_to) }}</td>
                    <td>{{ hoursLabel(row.overtime_agreement_monthly_limit_minutes) }}</td>
                    <td>{{ hoursLabel(row.overtime_agreement_yearly_limit_minutes) }}</td>
                    <td>{{ row.overtime_agreement_special_clause_flg ? 'あり' : 'なし' }}</td>
                    <td>{{ row.confirmed_flg ? '登録済み' : '未登録' }}</td>
                    <!--
                      **内容が未登録の行は、編集で確定させない。**
                      これは製品が置いた土台の行で、ずっと前から効いている
                      形になっている。ここへ内容を入れて確定すると、
                      **過去のすべての月が、いま入れた上限で判定される。**
                      適用開始日は編集で動かせないので、直す手立ても無い。
                      協定は「新しい協定を作る」から、いつから効くのかを
                      入れて登録する。
                    -->
                    <td>
                      <CButton
                        v-if="row.confirmed_flg"
                        type="button" size="sm" color="primary" variant="outline"
                        :disabled="isEditingRow(row)"
                        @click="editAgreement(row)"
                      >{{ isEditingRow(row) ? '編集中' : 'この協定を編集する' }}</CButton>
                      <span v-else class="agreement-history__placeholder">
                        内容が登録されていないため、編集できません。
                        「新しい協定を作る」から登録してください。
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p class="field-hint">
                過去の月は、<strong>その月に効いていた協定</strong>で判定します。
                協定を結び直したときは、前の行を書き換えずに新しい協定を1本足してください。
                前の行を書き換えると、当時の判定まで変わります。
              </p>
            </div>

            <!--
              いま何をしているのか。**「作る」と「編集する」を混ぜない。**
            -->
            <div class="agreement-mode" :class="'agreement-mode--' + agreementMode">
              <div>
                <strong>{{ agreementModeHeadline }}</strong>
                <small>{{ agreementModeDetail }}</small>
              </div>
              <CButton
                type="button" color="secondary" variant="outline"
                :disabled="agreementMode === 'create'"
                @click="startNewAgreement"
              >新しい協定を作る</CButton>
            </div>

            <!--
              **どちらの操作かを、入れる人に選んでもらう。**

              前は打った日から製品が推測していた——いま効いている協定の
              対象期間の中なら起算日を引き継ぎ、外なら新しい対象期間。
              **推測が外れても何も起きない。**1年ずれた日を打ち間違えると、
              そこから新しい1年が始まり、それまでに積み上がった時間が
              年間の上限から丸ごと外れる。画面には何も出ない。

              年360時間・年720時間・限度時間を超えられる回数は対象期間ごとに
              数え直すので、ここは判定そのものを動かす。
            -->
            <fieldset
              v-if="agreementMode === 'create'"
              class="agreement-period-mode"
            >
              <legend>この協定の位置づけ<span class="asterisk">*</span></legend>
              <label class="setting-switch-card" for="agreementPeriodModeNew">
                <input
                  id="agreementPeriodModeNew"
                  type="radio"
                  value="NEW_PERIOD"
                  v-model="agreement.period_mode"
                />
                <span>
                  <strong>新しい1年の対象期間を始める</strong>
                  <small>
                    協定を結び直して、年間の上限を数え直すときはこちら。
                    適用開始日が、年間の上限を数える起算日になります。
                  </small>
                </span>
              </label>
              <label class="setting-switch-card" for="agreementPeriodModeRevision">
                <input
                  id="agreementPeriodModeRevision"
                  type="radio"
                  value="REVISION"
                  v-model="agreement.period_mode"
                />
                <span>
                  <strong>いまの対象期間の中で内容を変える（改定）</strong>
                  <small>
                    年の途中で月の限度時間などを変えるときはこちら。
                    起算日はいまの協定から引き継ぎ、
                    <strong>それまでに積み上がった時間は年間の上限に残ります。</strong>
                    いまの協定は、この協定が始まる前日で自動的に終わります。
                  </small>
                </span>
              </label>
              <p v-if="agreement.period_mode === 'REVISION'" class="agreement-note">
                改定では、<strong>年の限度時間・特別条項・限度時間を超えられる回数は
                変えられません。</strong>これらは対象期間ぜんぶで数えるため、
                途中で変えるとどちらの数値で数えるのかが決まらなくなります。
                変えるときは、いまの対象期間が終わったあとに
                「新しい1年の対象期間を始める」で登録してください。
              </p>
            </fieldset>

            <CRow>
              <CCol sm="12" md="6">
                <!--
                  **日で受ける。**月だけを選ばせると、実際の起算日が
                  分からなくなる。5月15日から効く協定でも5月しか選べず、
                  5月1日として登録されてしまう。
                -->
                <ValidationProvider
                  name="適用開始日" rules="required" v-slot="provider"
                  :vid="'agreementValidFrom'"
                >
                  <label>適用開始日<span class="asterisk">*</span></label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="agreement.valid_from"
                    :disabled="agreementMode === 'edit'"
                  />
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
                <small v-if="agreementMode === 'edit'">
                  すでに登録した協定の適用開始日は動かせません。
                  日を打ち直したいときは、<strong>新しい協定を作って</strong>ください。
                </small>
                <small v-else>
                  協定書に書かれている、この協定が効き始める日を入れてください。
                </small>
              </CCol>
              <CCol sm="12" md="6">
                <ValidationProvider
                  name="有効期間の終わり" rules="required" v-slot="provider"
                  :vid="'agreementValidTo'"
                >
                  <label>有効期間の終わり<span class="asterisk">*</span></label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="agreement.valid_to"
                  />
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
                <small>
                  36協定には有効期間の定めが要ります（労働基準法36条2項5号）。
                  終わりが無いと、切れた協定と生きている協定を見分けられません。
                  <strong>対象期間の起算日から1年を越える日は入れられません。</strong>
                  越えると、年間の上限をどこで数え直すのかが決まらなくなります。
                  次の1年は「新しい1年の対象期間を始める」から入れてください。
                </small>
              </CCol>
            </CRow>

            <!--
              月の初日・末日でない日を打ったときに、その場で言う。
              **黙って月初へ丸めない。**丸めると、5月15日から効く協定が
              5月1日として登録され、入れた人には何も出ない。
            -->
            <div v-if="agreementPeriodWarning" class="agreement-period-warning">
              <strong>この期間では上限を判定できません</strong>
              <small>{{ agreementPeriodWarning }}</small>
            </div>

            <CRow>
              <!--
                **起算日は、適用開始日から作らない。**

                36協定届は「協定の有効期間」と「1年の起算日」を別の欄で
                書かせる。4月起算のまま10月に結び直す組は実際にあるので、
                適用開始日をそのまま起算日にすると**その協定を表せない。**
                しかも黙って同じ日へ変換するので、会社は自分の協定と違う
                期間で数えられていることに気づけない。

                とはいえ**同じ日であることのほうが多い。**だから
                適用開始日を入れた時点で同じ日を先に埋めておく
                （手で変えたあとは上書きしない）。多い形は何もしなくてよく、
                違う会社だけが直せばよい。
              -->
              <CCol sm="12" md="4">
                <template v-if="agreementMode === 'create' && agreement.period_mode === 'NEW_PERIOD'">
                  <ValidationProvider
                    name="1年の起算日" rules="required" v-slot="provider"
                    :vid="'agreementAnnualStart'"
                  >
                    <label>1年の起算日<span class="asterisk">*</span></label>
                    <input
                      type="date"
                      class="form-control"
                      v-model="agreement.annual_start_date"
                      @change="annualStartTouched = true"
                    />
                    <p class="error">{{ provider.errors[0] }}</p>
                  </ValidationProvider>
                  <small>
                    36協定届の<strong>「1年の起算日」の欄</strong>をそのまま入れてください。
                    年360時間・年720時間・限度時間を超えられる回数は、この日から1年で数えます。
                    適用開始日と同じことが多いので、同じ日を先に入れてあります。
                    <strong>4月起算のまま10月に結び直した協定</strong>のように違う場合は、
                    協定書のとおりに直してください。
                    月の初日で、適用開始日と同じ日かそれより前になります。
                  </small>
                </template>
                <template v-else>
                  <label>対象期間の起算日</label>
                  <p class="agreement-readonly">{{ annualStartLabel }}</p>
                  <small>
                    年の上限は、この日から1年で数えます。
                    <strong>いまの対象期間の中の改定では動かせません。</strong>
                    切り替わると、それまでに積み上がった時間が年の上限から消えてしまいます。
                  </small>
                </template>
              </CCol>
              <CCol sm="12" md="4">
                <ValidationProvider name="月の限度時間" rules="numeric|min_value:0|max_value:744" v-slot="provider">
                  <label>月の限度時間（時間）</label>
                  <CInput
                    type="number" min="0" max="744"
                    v-model="overtimeMonthlyLimitHours"
                    placeholder="45"
                    description="法定の原則値は45時間。休日労働は含みません。"
                  />
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </CCol>
              <CCol sm="12" md="4">
                <ValidationProvider name="年の限度時間" rules="numeric|min_value:0|max_value:8784" v-slot="provider">
                  <label>年の限度時間（時間）</label>
                  <CInput
                    type="number" min="0" max="8784"
                    v-model="overtimeYearlyLimitHours"
                    placeholder="360"
                    description="法定の原則値は360時間。休日労働は含みません。"
                  />
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </CCol>
            </CRow>

            <div class="switch-grid">
              <label class="setting-switch-card" for="overtimeVariableYearFlg">
                <input
                  id="overtimeVariableYearFlg" type="checkbox"
                  v-model="agreement.overtime_agreement_variable_year_flg"
                />
                <span>
                  <strong>1年単位の変形労働時間制の対象（対象期間が3か月超）</strong>
                  <small>対象にすると、法定の原則値が月42時間・年320時間になります。</small>
                </span>
              </label>
              <label class="setting-switch-card" for="overtimeSpecialClauseFlg">
                <input
                  id="overtimeSpecialClauseFlg" type="checkbox"
                  v-model="agreement.overtime_agreement_special_clause_flg"
                />
                <span>
                  <strong>特別条項がある</strong>
                  <small>臨時的な特別の事情があるときだけ、限度時間を超えられます。結んでいない場合はオフのままにしてください。</small>
                </span>
              </label>
            </div>

            <CRow>
              <CCol sm="12" md="6">
                <ValidationProvider name="特別条項の年上限" rules="numeric|min_value:0|max_value:8784" v-slot="provider">
                  <label>特別条項の年上限（時間）</label>
                  <CInput
                    type="number" min="0" max="8784"
                    v-model="overtimeSpecialYearlyLimitHours"
                    :disabled="!agreement.overtime_agreement_special_clause_flg"
                    placeholder="720"
                    description="法定の上限は720時間。休日労働は含みません。特別条項があるときだけ使います。"
                  />
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </CCol>
              <CCol sm="12" md="6">
                <ValidationProvider name="限度時間を超えられる回数" rules="numeric|min_value:0|max_value:12" v-slot="provider">
                  <label>限度時間を超えられる回数（年）</label>
                  <CInput
                    type="number" min="0" max="12"
                    v-model.number="agreement.overtime_agreement_exceed_month_count"
                    :disabled="!agreement.overtime_agreement_special_clause_flg"
                    placeholder="6"
                    description="法定の上限は年6回。特別条項があるときだけ使います。"
                  />
                  <p class="error">{{ provider.errors[0] }}</p>
                </ValidationProvider>
              </CCol>
            </CRow>

            <CAlert color="warning">
              <strong>法令により、特別条項の有無にかかわらず守るべき上限があります（時間外労働と休日労働の合計時間など）。</strong>
              これは協定で緩和できないため、設定項目にはありません。
            </CAlert>

            <ActionResultMessage
              :text="agreementSaveResult.text"
              :type="agreementSaveResult.type"
              :reveal="agreementRevealResult"
            />
            <div class="agreement-actions">
              <CButton
                v-if="agreementStale"
                type="button" color="danger" variant="outline"
                @click="reloadAgreement"
              >最新の状態を読み込み直す</CButton>
              <CButton type="submit" color="primary" :disabled="agreementSaving">
                {{ agreementMode === 'create' ? 'この内容で協定を登録する' : 'この協定を更新する' }}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </form>
    </ValidationObserver>

    <SettingUpdateModal
      v-if="showModal"
      :settings="form"
      :token-action="tokenAction"
      :loading="saving"
      @update="update"
      @close="showModal = false"
    />
  </div>
</template>

<script>
import api from '@/services/companySettings'
import { scrollToFirstInvalid } from '@/mixins/submitWhenValidMixin'
import SettingUpdateModal from './SettingUpdateModal'
import ActionResultMessage from '@/components/ActionResultMessage.vue'
import { createAttendanceSettingDefaults } from '@/utils/attendanceSettings'
import {
  overtimeAlertBlockerOf,
  overtimeAlertDeliveryNotice,
  overtimeAlertUsesManagementChannelOf
} from '@/utils/overtimeLimitStatus'
import {
  overtimeAgreementStatusClasses,
  overtimeAgreementStatusNotice
} from '@/utils/overtimeAgreementStatus'

/**
 * 会社情報の保存では**送らない**項目の頭。
 *
 * 36協定は別の口で保存する。ここを名指しの一覧にすると、サーバが項目を
 * 1つ増やしたときに黙って混ざるので、**頭で落とす。**
 * `overtime_alert_` と `overtime_calculation_` は会社情報側の設定なので
 * 残る（頭が違う）。
 */
const OVERTIME_AGREEMENT_PREFIXES = [
  'overtime_agreement_',
  'overtime_effective_'
]

/** その項目が36協定のものか。**画面の中で1か所だけで決める。** */
function isOvertimeAgreementKey (key) {
  return OVERTIME_AGREEMENT_PREFIXES.some(
    prefix => String(key).indexOf(prefix) === 0)
}

/**
 * 新しく作るときの操作。**サーバと同じ2つ。**
 *
 * 新しい1年の対象期間を始めるのか、いまの対象期間の中の改定なのか。
 * 名前がサーバとずれると、選んだ操作が届かないまま断られる。
 */
const AGREEMENT_PERIOD_MODES = ['NEW_PERIOD', 'REVISION']

/**
 * 新しく作る協定の初期値。**法定の原則値から始める。**
 *
 * いま効いている協定の値を写して始めない。写すと、去年の特別条項が
 * 見られないまま新しい協定へ引き継がれる——特別条項の有無ひとつで
 * 年の天井が360時間から720時間へ変わる。
 */
const emptyAgreement = () => ({
  id: null,
  lock_version: null,
  /*
   * 新しく作るときの操作。**既定を置かない。**
   *
   * 既定を置くと、選ばずに保存した人の協定がその扱いで登録される。
   * 新しい対象期間として入ったのか、いまの対象期間の改定として入ったのかで、
   * 年間の上限の数え方が変わる——取り違えると、それまでに積み上がった時間が
   * 上限から外れる。**外れたことは画面に出ない。**
   */
  period_mode: '',
  valid_from: '',
  valid_to: '',
  annual_start_date: '',
  confirmed_flg: false,
  overtime_agreement_monthly_limit_minutes: 2700,
  overtime_agreement_yearly_limit_minutes: 21600,
  overtime_agreement_variable_year_flg: false,
  overtime_agreement_special_clause_flg: false,
  overtime_agreement_special_yearly_limit_minutes: 43200,
  overtime_agreement_exceed_month_count: 6
})

/** 履歴の1行を、編集する形へ写す。**全項目を写す。** */
const agreementFromRow = row => {
  const source = row || {}
  const base = emptyAgreement()
  const pick = (key, fallback) =>
    source[key] === undefined || source[key] === null
      ? fallback : source[key]
  return {
    id: pick('id', null),
    lock_version: pick('lock_version', null),
    valid_from: String(pick('valid_from', '')),
    valid_to: String(pick('valid_to', '')),
    annual_start_date: String(pick('annual_start_date', '')),
    confirmed_flg: Boolean(pick('confirmed_flg', false)),
    overtime_agreement_monthly_limit_minutes: pick(
      'overtime_agreement_monthly_limit_minutes',
      base.overtime_agreement_monthly_limit_minutes),
    overtime_agreement_yearly_limit_minutes: pick(
      'overtime_agreement_yearly_limit_minutes',
      base.overtime_agreement_yearly_limit_minutes),
    overtime_agreement_variable_year_flg: Boolean(pick(
      'overtime_agreement_variable_year_flg', false)),
    overtime_agreement_special_clause_flg: Boolean(pick(
      'overtime_agreement_special_clause_flg', false)),
    overtime_agreement_special_yearly_limit_minutes: pick(
      'overtime_agreement_special_yearly_limit_minutes',
      base.overtime_agreement_special_yearly_limit_minutes),
    overtime_agreement_exceed_month_count: pick(
      'overtime_agreement_exceed_month_count',
      base.overtime_agreement_exceed_month_count)
  }
}

const emptyForm = () => ({
  company_name: '', company_email: '', telephone: '',
  /* 労務法令の判定を出してよい段階か。**既定は出さない側。** */
  legal_compliance_enabled: false,
  /* **空は「出さない」。**既定値を持たせない。 */
  dashboard_calendar_embed_url: '',
  /* 提出物の締め。**空は「決めていない」。** */
  submission_due_kind: '',
  submission_due_day: '',
  submission_due_items: '',
  ...createAttendanceSettingDefaults(),
  slack_feature_enabled: false, slack_notification_flg: false,
  slack_token_configured: false, slack_token: '', clear_slack_token: false,
  slack_channel: '', pending_approval_notification_flg: false,
  pending_approval_slack_channel: '',
  management_daily_notification_flg: false,
  management_daily_slack_channel: '',
  /*
   * 36協定アラート。**空は「決めていない」。既定へ倒さない。**
   * 既定を入れるのは列のほうで、画面はサーバが返した値をそのまま持つ。
   */
  overtime_alert_notification_flg: false,
  overtime_alert_slack_channel: '',
  overtime_alert_threshold_percent: '',
  overtime_calculation_due_kind: '',
  overtime_calculation_due_day: '',
  business_start_time: '09:00', business_end_time: '18:00',
  daily_schedule_notification_lead_minutes: 30,
  pending_approval_notification_lead_minutes: 0,
  pending_leave_reminder_lead_minutes: 30,
  /*
   * **36協定はここに入れない。**
   *
   * この画面は保存のとき form を丸ごと送る。協定をここへ置くと、
   * 会社名を1文字直しただけの保存に協定の全項目が乗る。乗ったうえで
   * 適用開始日が空だと「いま効いている協定を書き換える」ほうへ入るので、
   * **いま効いている協定が画面の値で丸ごと上書きされる。**
   *
   * 協定は this.agreement に分けて持ち、専用の口へ送る。
   */
})

export default {
  name: 'TenantSettingUpdate',
  components: { SettingUpdateModal, ActionResultMessage },
  data () {
    return {
      form: emptyForm(),
      original: null,
      loadingInitial: false,
      saving: false,
      showModal: false,
      errorMessage: '',
      /*
       * 36協定。**form とは別に持つ。**同じ入れ物に置くと、会社情報の
       * 保存に協定が乗る。乗ったことは画面のどこにも出ない。
       */
      agreement: emptyAgreement(),
      /** 読み込んだときの姿。**打ちかけを黙って捨てないため。** */
      agreementOriginal: emptyAgreement(),
      /**
       * 1年の起算日を、手で書き換えたか。
       *
       * <b>先に埋めるのは親切、上書きし続けるのはおせっかい。</b>
       * 適用開始日と同じ日を先に埋めておくと、多い形は何もしなくてよい。
       * ただし一度手で直したあとに適用開始日を触ると、直した日が
       * 黙って戻る——**戻ったことは画面に出ない。**だから覚えておく。
       */
      annualStartTouched: false,
      /** 'create'（新しく作る）か 'edit'（この協定を編集する）。 */
      agreementMode: 'create',
      /** 登録されている協定。**サーバが返す並びと印をそのまま使う。** */
      agreementHistory: [],
      /** サーバが返した「判定できているか」。画面では計算しない。 */
      agreementStatus: '',
      agreementStatusMessage: '',
      agreementSaving: false,
      agreementError: '',
      agreementMessage: '',
      /**
       * agreementError が［保存］で起きたものか。true なら［保存］のすぐ上に、
       * false（履歴の［編集］で断ったとき）なら協定のカードの頭に出す。
       */
      agreementErrorNearSave: false,
      /**
       * ［保存］の結果が出たとき、そこまで画面を動かすか。入力の不備で
       * 引っかかった欄へ動かすときは、その動きを打ち消さないよう false にする。
       */
      agreementRevealResult: true,
      /** 断られたのが「ほかの人が先に変えた」ためか。読み直しを勧める。 */
      agreementStale: false,
      timeFields: [
        { key: 'pre_buffer', label: '定時前打刻猶予時間', description: '指定がない場合は00:00として計算され、打刻の猶予時間として使用されます。' },
        { key: 'post_buffer', label: '定時後打刻猶予時間', description: '指定がない場合は00:00として計算されます。追加休憩を有効にした場合もこの値が使用されます。' },
        { key: 'work_time_unit', label: '時間外の切り上げ単位', description: '指定がない場合は00:01として計算されます。00:00は指定できません。' },
        { key: 'work_basic', label: '規定勤務時間', description: '指定がない場合は08:00として計算されます。所定労働時間が異なる場合は変更してください。' },
        { key: 'break_basic', label: '規定休憩時間', description: '指定がない場合は01:00として計算されます。実際の休憩時間に合わせて変更してください。' }
      ],
      leadFields: [
        {
          key: 'daily_schedule_notification_lead_minutes',
          label: '当日の休暇予定を始業の何分前に送るか',
          description: '指定がない場合は30分前になります。管理部門とプロジェクトの両方の通知に適用されます。'
        },
        {
          key: 'pending_approval_notification_lead_minutes',
          label: '承認待ちのお知らせを始業の何分前に送るか',
          description: '指定がない場合は0分（始業ちょうど）になります。'
        },
        {
          key: 'pending_leave_reminder_lead_minutes',
          label: '明日の未承認休暇を終業の何分前に送るか',
          description: '指定がない場合は30分前になります。休業日でも通知が送信されます。'
        }
      ]
    }
  },
  watch: {
    /**
     * 適用開始日を入れたら、1年の起算日に同じ日を先に入れておく。
     *
     * <p>36協定届では別の欄だが、<b>同じ日であることのほうが多い。</b>
     * 多い形で2回同じ日を打たせない。違う会社（4月起算のまま10月に
     * 結び直した、など）だけが直せばよい。</p>
     *
     * <p><b>手で直したあとは上書きしない。</b>上書きすると、直した日が
     * 適用開始日を触るたびに黙って戻る。</p>
     */
    'agreement.valid_from' (value) {
      if (this.agreementMode !== 'create') { return }
      if (this.annualStartTouched) { return }
      this.agreement.annual_start_date = String(value || '')
    },
    /** 位置づけを選び直したら、先に埋めた日もそこから作り直す。 */
    'agreement.period_mode' (value) {
      if (this.agreementMode !== 'create') { return }
      if (this.annualStartTouched) { return }
      this.agreement.annual_start_date = value === 'NEW_PERIOD'
        ? String(this.agreement.valid_from || '')
        : ''
    }
  },
  computed: {
    /** ［保存］のすぐ上に出す、36協定の保存の結果。 */
    agreementSaveResult () {
      if (this.agreementError && this.agreementErrorNearSave) {
        return { type: 'danger', text: this.agreementError }
      }
      return { type: 'success', text: this.agreementMessage }
    },
    /**
     * 予告の割合の説明。
     *
     * **入れた割合が何時間になるかを、その場で出す。**
     * 「80%」と言われても、45時間の80%が36時間だと
     * すぐには出てこない。
     */
    overtimeAlertThresholdHint () {
      const percent = Number(this.form.overtime_alert_threshold_percent)
      if (!percent) {
        return '空なら知らせません。法令の上限（月45時間など）を超えたときは、'
          + 'この設定に関わらず知らせます。'
      }
      const limit = Number(this.form.overtime_agreement_monthly_limit_minutes)
      if (!limit) {
        return '月の限度時間の' + percent + '%に達した人を知らせます。'
          + '（36協定を登録すると、何時間になるかをここに出します）'
      }
      const minutes = Math.floor(limit * percent / 100)
      const hours = Math.floor(minutes / 60)
      const rest = String(minutes % 60).padStart(2, '0')
      return '月の限度時間の' + percent + '% = ' + hours + '時間' + rest + '分'
        + 'に達した人を知らせます。'
    },

    /**
     * 労務法令の判定を出してよい段階か。**サーバの応答から読む。**
     *
     * <p>front は設定を直接読まない。<b>既定は出さない側</b>——古いサーバや
     * 取得の失敗で欄が無いときに出す側へ倒すと、入れても判定に使われない
     * 欄が並ぶ。</p>
     */
    legalComplianceEnabled () {
      return Boolean(this.form && this.form.legal_compliance_enabled)
    },
    /*
     * **36協定アラートは入れない。**
     *
     * ほかの3つは会社が「送りたい」と決めて立てるフラグなので、決めた以上は
     * トークンが要る。36協定の上限は会社が選べるものではなく既定でオン
     * なので、ここへ入れると**Slackを使っていない会社は会社名すら
     * 直せなくなる。**既定で立っているフラグが、関係のない保存を人質に取る。
     */
    anySlackEnabled () {
      return Boolean(
        this.form.slack_notification_flg ||
        this.form.pending_approval_notification_flg ||
        this.form.management_daily_notification_flg
      )
    },
    /**
     * いまの入力なら届くか。**打っているそばから見せる。**
     *
     * 保存してから「届きません」と言われると、直すのに1往復かかる。
     * 決めているのはサーバで、保存すれば同じ判定が返る。
     */
    /**
     * 専用の通知先を空にしたら、どこへ届くのか。
     *
     * **管理部門向け朝通知を止めていれば、どこへも届かない。**止めても
     * チャンネルIDの値はこの画面に残るので、「同じチャンネルへ送ります」と
     * 書き続けると、実際には1通も出ないのに出る顔をした案内になる。
     */
    overtimeAlertChannelHint () {
      if (this.form.management_daily_notification_flg &&
          String(this.form.management_daily_slack_channel || '').trim()) {
        return '空欄のままなら、管理部門向け朝通知と同じチャンネルへお送りします。'
      }
      return '空欄のままだと、通知は送信されません。'
        + '管理部門向け朝通知をお使いの場合は、そちらのチャンネルへお送りします。'
    },
    overtimeAlertNotice () {
      /*
       * **送信の失敗はここでは出さない。**この画面は編集中の値から
       * 「いま保存したらどうなるか」を出す場所で、過去の送信結果は
       * 編集で変わらない。参照画面（Setting.vue）が出す。
       */
      return overtimeAlertDeliveryNotice(
        overtimeAlertBlockerOf(this.form),
        overtimeAlertUsesManagementChannelOf(this.form)
      )
    },
    tokenAction () {
      if (this.form.clear_slack_token) return '保存済みトークンを削除'
      if (this.form.slack_token) return this.form.slack_token_configured ? 'トークンを変更' : 'トークンを新規設定'
      return this.form.slack_token_configured ? '保存済みトークンを維持' : 'トークン未設定'
    },
    /**
     * いま判定できているのか。**サーバが返した状態をそのまま読む。**
     *
     * 画面で「協定があるか」を数え直すと、判定が読む1本と食い違う。
     * 食い違っても何も落ちないので、気づけるのは超過を見逃したあと。
     */
    agreementStatusNotice () {
      return overtimeAgreementStatusNotice(
        this.agreementStatus, this.agreementStatusMessage)
    },
    /**
     * 帯に付けるクラス。**7つの状態が、どれも同じ見た目にならないように。**
     *
     * 「判定できなかった」が「問題なし」と同じ顔になると、見ていないことが
     * 見えなくなる。
     */
    agreementStatusClasses () {
      return overtimeAgreementStatusClasses(this.agreementStatusNotice)
    },
    agreementModeHeadline () {
      return this.agreementMode === 'create'
        ? '新しい協定を作っています'
        : '登録済みの協定を編集しています'
    },
    agreementModeDetail () {
      if (this.agreementMode === 'create') {
        return '入力した内容で、協定を1本あたらしく登録します。'
          + 'すでに登録されている協定は書き換わりません。'
      }
      return (this.agreement.valid_from
        ? this.dayLabel(this.agreement.valid_from) + 'から効く協定'
        : '選んだ協定')
        + 'の内容を書き換えます。適用開始日は動かせません。'
    },
    /** 対象期間の起算日。**入力させず、サーバが決めたものを見せるだけ。** */
    annualStartLabel () {
      const value = String(this.agreement.annual_start_date || '')
      if (!value) {
        if (this.agreementMode !== 'create') { return '記録されていません' }
        /*
         * **どちらの操作かで、起算日の決まり方が変わる。**選ぶ前に
         * 「登録すると決まります」とだけ出すと、どちらを選んでも同じに
         * 見える。ここが判定を動かすところなので、選んだ結果を先に書く。
         */
        if (this.agreement.period_mode === 'REVISION') {
          return 'いまの協定の起算日を引き継ぎます'
        }
        if (this.agreement.period_mode === 'NEW_PERIOD') {
          return this.agreement.valid_from
            ? this.dayLabel(this.agreement.valid_from) + '（適用開始日と同じ）'
            : '適用開始日と同じ日になります'
        }
        return '上で位置づけを選ぶと決まります'
      }
      return this.dayLabel(value)
    },
    /**
     * 月の初日・末日でない日を打っていないか。**その場で言う。**
     *
     * 黙って月初へ丸めない。丸めると、5月15日から効く協定が5月1日として
     * 登録され、**5月がまるごとその協定で判定される**のに、入れた人には
     * 何も出ない。判定は暦月ごとなので、月の途中で始まる協定は
     * いまのしくみで計算できない。
     */
    agreementPeriodWarning () {
      const notes = []
      if (this.agreement.valid_from &&
          !this.isMonthStart(this.agreement.valid_from)) {
        notes.push('適用開始日が月の初日ではありません。')
      }
      if (this.agreement.valid_to &&
          !this.isMonthEnd(this.agreement.valid_to)) {
        notes.push('有効期間の終わりが月の末日ではありません。')
      }
      if (!notes.length) {
        /*
         * **対象期間からはみ出していることも、押す前に言う。**
         * 押してから断ると、押した人には何が悪かったのか結び付かない。
         * 月末かどうかとは別の話なので、そちらが片付いてから出す。
         */
        return this.beyondAnnualPeriod()
      }
      return notes.join('')
        + '上限の判定は暦月ごとに行うため、月の途中で始まる（終わる）協定は'
        + 'その月をどちらの協定で判定するかが決まりません。'
        + 'このままでは登録できないため、月の初日から月の末日までで'
        + '入力し直してください。'
    },
    /*
     * 36協定の限度時間。**打つのは時間、持つのは分。**
     *
     * 協定は分のまま持つ。時間の欄をそのまま入れ物へ足すと、送るときに
     * 混ざる。空欄は null にして、**サーバが法定の原則値へ寄せる**。
     * ここで既定値を書き戻すと、消している最中に「45」が勝手に現れる。
     */
    overtimeMonthlyLimitHours: {
      get () { return this.hoursOf(this.agreement.overtime_agreement_monthly_limit_minutes) },
      set (value) { this.agreement.overtime_agreement_monthly_limit_minutes = this.minutesOf(value) }
    },
    overtimeYearlyLimitHours: {
      get () { return this.hoursOf(this.agreement.overtime_agreement_yearly_limit_minutes) },
      set (value) { this.agreement.overtime_agreement_yearly_limit_minutes = this.minutesOf(value) }
    },
    overtimeSpecialYearlyLimitHours: {
      get () { return this.hoursOf(this.agreement.overtime_agreement_special_yearly_limit_minutes) },
      set (value) { this.agreement.overtime_agreement_special_yearly_limit_minutes = this.minutesOf(value) }
    }
  },
  mounted () {
    this.loadSettings()
  },
  methods: {
    async loadSettings () {
      this.loadingInitial = true
      this.errorMessage = ''
      try {
        const result = await api.get()
        // 通知開始営業日（pending_approval_start_business_day）は撤去した。
        // 保存も検証もできるのに、承認待ち通知の送信判定が一度も読んで
        // いなかった。「3営業日目から」と設定して保存に成功しても初日から
        // 届く。**効かない設定を保存できる画面は、未実装ではなく虚偽**という
        // 指摘（2026-08-15・配置再開条件5）。承認待ちは翌日そのまま送れば
        // よいという業務判断で、実装ではなく撤去を選んだ。
        const received = result || {}
        this.form = Object.assign(emptyForm(), received, {
          slack_token: '',
          clear_slack_token: false
        })
        /*
         * **協定を form から抜く。**残すと、会社情報の保存に乗る。
         * 送るときに落とす守りも別に置いてあるが、そもそも持たない。
         */
        Object.keys(this.form).forEach(key => {
          if (isOvertimeAgreementKey(key)) { delete this.form[key] }
        })
        this.applyAgreement(received)
        this.original = JSON.parse(JSON.stringify(this.form))
      } catch (error) {
        this.handleError(error, '自社設定を取得できませんでした。')
      } finally {
        this.loadingInitial = false
      }
    },
    async openModal () {
      this.errorMessage = ''
      const valid = await this.$refs.observer.validate()
      if (!valid) {
        this.errorMessage = '入力内容にエラーがあります。赤字で表示された項目を確認してください。'
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }
      if (!this.validateSlackConditions()) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }
      this.showModal = true
    },
    validateSlackConditions () {
      if (!this.form.slack_feature_enabled && this.anySlackEnabled) {
        this.errorMessage = '現在のご契約や設定では、Slack通知をオンにできません。'
        return false
      }
      const hasToken = !this.form.clear_slack_token &&
        (this.form.slack_token_configured || Boolean(this.form.slack_token))
      if (this.anySlackEnabled && !hasToken) {
        this.errorMessage = 'Slack通知をオンにする場合はSlack Botトークンを設定してください。'
        return false
      }
      if (this.form.slack_notification_flg && !this.form.slack_channel) {
        this.errorMessage = '即時通知をオンにする場合は通知先チャンネルIDを入力してください。'
        return false
      }
      if (this.form.pending_approval_notification_flg && !this.form.pending_approval_slack_channel) {
        this.errorMessage = '承認待ちリマインドをオンにする場合は通知先チャンネルIDを入力してください。'
        return false
      }
      if (this.form.management_daily_notification_flg && !this.form.management_daily_slack_channel) {
        this.errorMessage = '管理部門向け朝通知をオンにする場合は通知先チャンネルIDを入力してください。'
        return false
      }
      /*
       * **36協定アラートの通知先と算定期日は、空でも断らない。**
       *
       * 通知先が空なら管理部門向けへ送るし、どちらも空なら本当に届かない
       * が、そこで保存を断ると既定で立っているフラグのせいでこの画面そのものが
       * 保存できなくなる。断る代わりに、上の帯で「届いていません」と
       * 出している。**黙って消えるのを止めたいのであって、保存を
       * 止めたいのではない。**
       */
      if (Boolean(this.form.overtime_calculation_due_kind) !== Boolean(this.form.overtime_calculation_due_day)) {
        this.errorMessage = '算定期日は、数え方と日の両方を設定してください。'
        return false
      }
      if (this.form.clear_slack_token && this.anySlackEnabled) {
        this.errorMessage = 'Slackトークンを削除する場合は、すべてのSlack通知をオフにしてください。'
        return false
      }
      return true
    },
    async update () {
      this.saving = true
      this.errorMessage = ''
      try {
        // 読んだ値をそのまま返す。画面に入力欄が無いので、ここで値を
        // 作ると、開いて保存しただけで設定が変わってしまう。
        const payload = Object.assign({}, this.form)
        delete payload.slack_feature_enabled
        delete payload.slack_token_configured
        // 「届くか」はサーバが決めて返すもの。**送り返さない。**
        delete payload.overtime_alert_delivery_blocker
        delete payload.overtime_alert_uses_management_channel
        /*
         * **36協定は1項目も送らない。**
         *
         * この保存は会社情報のためのもの。協定が混ざると、会社名を
         * 1文字直しただけで協定の全項目が飛ぶ。しかも適用開始日が空だと
         * サーバは「いま効いている協定を書き換える」ほうへ入るので、
         * **いま効いている協定が画面の値で丸ごと上書きされる。**
         * 読み込むときに form から抜いてあるが、ここでも落とす
         * ——抜き忘れを、この画面のいちばん外側で止める。
         */
        Object.keys(payload).forEach(key => {
          if (isOvertimeAgreementKey(key)) { delete payload[key] }
        })
        await api.update(payload)
        this.showModal = false
        this.$router.push({ name: '設定' })
      } catch (error) {
        this.showModal = false
        this.handleError(error, '自社設定を更新できませんでした。')
      } finally {
        this.saving = false
      }
    },
    /**
     * サーバが返した協定を、画面へ移す。
     *
     * <b>最初にどちらの操作で開くかも、ここで決める。</b>会社が自分で
     * 登録した協定があるなら、開いた直後は「その協定を編集する」。
     * まだ登録されていない（製品が置いただけの行しか無い）なら、
     * 「新しい協定を作る」で開く——仮の行を書き換えさせると、
     * 実際の協定の適用開始日を入れる場所が無くなる。
     */
    applyAgreement (received) {
      const source = received || {}
      this.agreementHistory = Array.isArray(source.overtime_agreement_history)
        ? source.overtime_agreement_history
        : []
      this.agreementStatus = String(source.overtime_agreement_status || '')
      this.agreementStatusMessage =
        String(source.overtime_agreement_status_message || '')

      const current = this.agreementHistory.find(row => row && row.current_flg)
      if (current && current.confirmed_flg && current.id != null) {
        this.editAgreement(current)
      } else {
        this.startNewAgreement()
      }
    },
    /**
     * 新しい協定を作る。**いまの値を写して始めない。**
     *
     * 写すと、去年の特別条項が見られないまま新しい協定へ引き継がれる。
     * 特別条項の有無ひとつで年の天井が360時間から720時間へ変わる。
     */
    startNewAgreement () {
      this.agreementMode = 'create'
      this.agreement = emptyAgreement()
      this.agreementOriginal = emptyAgreement()
      // 新しく作り始めるので、手で直した記録も消す。
      this.annualStartTouched = false
      this.agreementError = ''
      this.agreementErrorNearSave = false
      this.agreementMessage = ''
      this.agreementStale = false
    },
    /**
     * 履歴の1行を、そのまま編集の欄へ読み込む。
     *
     * <b>ここが無かった。</b>履歴は表示するだけで、会社が触れるのは
     * 「いま効いている1本」だけだった。月だけ打ち直して保存すると、
     * 画面に出ていた現在の値でその協定が上書きされる。
     */
    editAgreement (row) {
      /*
       * **内容が未登録の行は、編集で確定させない。**
       *
       * それは製品が置いた土台の行で、ずっと前から効いている形になって
       * いる。ここへ内容を入れて確定すると、**過去のすべての月が、いま
       * 入れた上限で判定される。**適用開始日は編集で動かせないので、
       * 入れた人には直す手立てが無い。サーバも同じところで断る
       * （断りが2枚あるのは、片方が消えても抜けないようにするため）。
       */
      if (!row || !row.confirmed_flg) {
        this.startNewAgreement()
        this.agreementError =
          '内容が登録されていない協定は編集できません。' +
          '適用開始日を指定して、新しく登録してください。' +
          '（そのまま内容を入れると、過去のすべての月が' +
          'いま入れた上限で判定されます）'
        return
      }
      this.agreementMode = 'edit'
      this.agreement = agreementFromRow(row)
      this.agreementOriginal = agreementFromRow(row)
      this.agreementError = ''
      this.agreementErrorNearSave = false
      this.agreementMessage = ''
      this.agreementStale = false
    },
    /** いまこの行を編集しているか。**一覧のどこを触っているかを見せる。** */
    isEditingRow (row) {
      return this.agreementMode === 'edit' &&
        row != null && row.id != null &&
        String(row.id) === String(this.agreement.id)
    },
    agreementRowKey (row, index) {
      const source = row || {}
      if (source.id != null) { return 'agreement-' + source.id }
      return 'agreement-' + index + '-' + String(source.valid_from || '')
    },
    /** 読み直す。**ほかの人が先に変えたときに、いまの内容へ戻す入口。** */
    async reloadAgreement () {
      this.agreementError = ''
      this.agreementErrorNearSave = false
      this.agreementMessage = ''
      this.agreementStale = false
      await this.loadSettings()
    },
    /**
     * 36協定を1本、保存する。**会社情報の保存とは別の口へ送る。**
     *
     * 送るのは協定の項目だけ。会社名も通知の設定も混ぜない。
     */
    async saveAgreement () {
      // 入力の確認を待つあいだに押し直されても2本目を走らせないよう、先に保存中にする
      if (this.agreementSaving) return
      this.agreementSaving = true
      this.agreementError = ''
      this.agreementErrorNearSave = true
      this.agreementRevealResult = true
      this.agreementMessage = ''
      this.agreementStale = false

      try {
        const observer = this.$refs.agreementObserver
        if (observer && typeof observer.validate === 'function') {
          const valid = await observer.validate()
          if (!valid) {
            this.agreementError =
              '入力内容にエラーがあります。赤字で表示された項目を確認してください。'
            // 保存ボタンは節の下、案内は節の上。押した場所から見えるよう、引っかかった欄へ動かす
            // （［保存］の上に出した文のほうへ動いて、この動きを打ち消さないようにする）
            this.agreementRevealResult = false
            scrollToFirstInvalid(this, observer)
            return
          }
        }
        if (!this.validateAgreement()) { return }

        const payload = this.agreementPayload()
        const result = this.agreementMode === 'create'
          ? await api.createOvertimeAgreement(payload)
          : await api.updateOvertimeAgreement(payload)
        /*
         * **書き終えた形を、サーバからもう一度読み直す。**
         * 手元の値をそのまま画面へ残すと、サーバが導いた対象期間の起算日や
         * 「いま効いている」の印が古いままになる。
         */
        await this.loadSettings()
        this.agreementMessage = this.savedMessage(result)
      } catch (error) {
        this.handleAgreementError(error)
      } finally {
        this.agreementSaving = false
      }
    },
    /**
     * 送る前に、画面で分かることを断る。
     *
     * <b>サーバも同じことを断る。</b>ここは断られる前に言うためのもので、
     * 守りそのものではない。とくに適用開始日は、空のまま送ると
     * 「いつから効く協定なのか」が決まらない。
     */
    validateAgreement () {
      if (this.agreementMode === 'create') {
        if (!String(this.agreement.valid_from || '').trim()) {
          this.agreementError = '36協定の適用開始日を入力してください。'
            + '（いつから効く協定なのかが決まらないと、'
            + 'どの月をこの協定で見るのかが決まりません）'
          return false
        }
        if (String(this.agreement.period_mode || '') === 'NEW_PERIOD') {
          const annualStart = String(this.agreement.annual_start_date || '').trim()
          if (!annualStart) {
            this.agreementError = '36協定の「1年の起算日」を入力してください。'
              + '（36協定届では有効期間とは別の欄です。'
              + '年360時間・年720時間・限度時間を超えられる回数を'
              + 'どこから数え直すのかが、この日で決まります）'
            return false
          }
          if (!this.isMonthStart(annualStart)) {
            this.agreementError = '36協定の1年の起算日は月の初日で入力してください。'
              + '（上限の判定は月ごとなので、月の途中から始まる対象期間は'
              + '半月ぶんの時間外労働がどちらの年に入るのかが決まりません）'
            return false
          }
          if (annualStart > String(this.agreement.valid_from || '')) {
            this.agreementError = '36協定の1年の起算日が、適用開始日より後になっています。'
              + '起算日より前の月は年間の上限のどこにも入らないため、'
              + '起算日は適用開始日と同じか、それより前の日付にしてください。'
            return false
          }
        }
        if (!AGREEMENT_PERIOD_MODES.includes(
          String(this.agreement.period_mode || ''))) {
          this.agreementError =
            '「この協定の位置づけ」を選択してください。'
            + '（新しい1年の対象期間を始めるのか、'
            + 'いまの対象期間の中の改定なのかで、'
            + '年間の上限の数え方が変わります）'
          return false
        }
      } else if (this.agreement.id == null ||
          this.agreement.lock_version == null) {
        this.agreementError = 'どの協定を変えるのかが分からなくなりました。'
          + '画面を再読み込みしてから、もう一度お試しください。'
        this.agreementStale = true
        return false
      }
      if (!String(this.agreement.valid_to || '').trim()) {
        this.agreementError = '36協定の有効期間の終わりを入力してください。'
          + '（終わりが無いと、切れた協定と生きている協定を見分けられません）'
        return false
      }
      if (this.agreement.valid_from && this.agreement.valid_to &&
          String(this.agreement.valid_to) < String(this.agreement.valid_from)) {
        this.agreementError =
          '36協定の有効期間の終わりが、始まりより前になっています。'
        return false
      }
      /*
       * **対象期間（起算日から1年）からはみ出させない。**
       *
       * 有効期間の終わりだけを打ち直して協定を1年延ばすのは、ふつうの
       * 操作。ところがここを通すと表の CHECK まで届き、押した人に返るのは
       * 「システムエラーが発生しました。管理者へお問い合わせください。」
       * だけになる。**何が悪いのかも、どう直せばよいのかも出ない。**
       *
       * 起算日がまだ決まっていない（新規で、登録すると決まる）ときは
       * 見ない。決まっていない相手と比べても言えることが無い。
       */
      const overrun = this.beyondAnnualPeriod()
      if (overrun) {
        this.agreementError = overrun
        return false
      }
      return true
    },
    /**
     * 有効期間の終わりが、対象期間（起算日から1年）を越えていないか。
     *
     * <p>越えていれば、そのまま画面に出す文言を返す。越えていなければ
     * 空文字。<b>越えているかどうかを黙って丸めない</b>——丸めると、
     * 会社が入れた協定と登録された協定が別物になる。</p>
     */
    beyondAnnualPeriod () {
      const annualStart = String(this.agreement.annual_start_date || '').trim()
      const validTo = String(this.agreement.valid_to || '').trim()
      if (!annualStart || !validTo) { return '' }
      const matched = annualStart.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (!matched) { return '' }
      // 起算日のちょうど1年後の日。**その日まで**が対象期間。
      const limit = [
        String(Number(matched[1]) + 1).padStart(4, '0'),
        matched[2], matched[3]
      ].join('-')
      if (validTo <= limit) { return '' }
      return '36協定の有効期間が、対象期間（'
        + annualStart + 'から1年）に収まっていません。'
        + '1年を超える期間で結んだ協定は、年間の上限をどこで数え直すのかが'
        + '決まらないため、1年ごとに分けて登録してください。'
        + '（いまの期間の終わりまでを1本にして、続きは'
        + '「新しい1年の対象期間を始める」から登録してください）'
    },
    /**
     * 送る中身。**協定の項目だけ。**
     *
     * 編集では適用開始日を送らない。動かせるようにすると、去年の協定の
     * 開始日を今年へずらして「去年は協定が無かったこと」にできる。
     */
    agreementPayload () {
      const payload = {
        validTo: String(this.agreement.valid_to || ''),
        monthlyLimitMinutes:
          this.agreement.overtime_agreement_monthly_limit_minutes,
        yearlyLimitMinutes:
          this.agreement.overtime_agreement_yearly_limit_minutes,
        variableYearFlg:
          Boolean(this.agreement.overtime_agreement_variable_year_flg),
        specialClauseFlg:
          Boolean(this.agreement.overtime_agreement_special_clause_flg),
        specialYearlyLimitMinutes:
          this.agreement.overtime_agreement_special_yearly_limit_minutes,
        exceedMonthCount:
          this.agreement.overtime_agreement_exceed_month_count
      }
      if (this.agreementMode === 'create') {
        payload.validFrom = String(this.agreement.valid_from || '')
        // **どちらの操作かを送る。**サーバに推測させない。
        payload.periodMode = String(this.agreement.period_mode || '')
        /*
         * **1年の起算日は、新しい対象期間のときだけ送る。**
         *
         * 改定は前の協定から引き継ぐもので、ここで動かせると対象期間の
         * 途中で年の数え直しが始まる。それまでに積み上がった時間が
         * 年の上限から丸ごと消える。
         */
        if (String(this.agreement.period_mode || '') === 'NEW_PERIOD') {
          payload.annualStartDate =
            String(this.agreement.annual_start_date || '')
        }
      } else {
        payload.id = this.agreement.id
        payload.lockVersion = this.agreement.lock_version
      }
      return payload
    },
    /**
     * 保存できたときに出す言葉。
     *
     * <b>変わっていなければ、変わっていないと言う。</b>「更新しました」と
     * 出し続けると、本当に変えた1回が埋もれる。
     */
    savedMessage (result) {
      const operation = result && result.operation
      if (operation === 'NONE') {
        return '協定の内容は変わっていません。'
      }
      /*
       * **サーバが返した操作を先に見る。**保存のあと画面を読み直すので、
       * このときの agreementMode はもう 'edit' に戻っている。画面の状態で
       * 決めると、改定として登録したことが「更新しました」に化ける。
       */
      if (operation === 'REVISION') {
        const closedAt = result && result.closedPreviousValidTo
        return 'いまの対象期間の中の改定として登録しました。'
          + '年間の上限を数える起算日は変わりません。'
          + (closedAt
            ? '前の協定は ' + this.dayLabel(closedAt) + ' で終わりにしました。'
            : '')
      }
      if (operation === 'UPDATE' || this.agreementMode === 'edit') {
        return '協定の内容を更新しました。'
      }
      return '新しい協定を登録しました。'
        + 'この協定が効く月から、登録した内容で上限を見ます。'
    },
    /**
     * 断られたときに、利用者の言葉で出す。
     *
     * <b>版が合わないときは、上書きさせない。</b>上書きすると前の人の
     * 変更が黙って消える。消えたことは誰にも出ない。
     */
    handleAgreementError (error) {
      const status = error && error.response && error.response.status
      if (status === 409) {
        this.agreementError = 'ほかの方が先に変更しました。'
          + 'お手数ですが、最新の状態を読み込み直してからやり直してください。'
        this.agreementStale = true
        return
      }
      const detail = error && error.response && error.response.data &&
        error.response.data.message
      this.agreementError = detail || '36協定を保存できませんでした。'
    },
    /** 月の初日か。**打った日をここで丸めない。** */
    isMonthStart (value) {
      const matched = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
      return Boolean(matched) && Number(matched[3]) === 1
    },
    /** 月の末日か。うるう年もその月の日数で見る。 */
    isMonthEnd (value) {
      const matched = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (!matched) { return false }
      const year = Number(matched[1])
      const month = Number(matched[2])
      if (month < 1 || month > 12) { return false }
      const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
      return Number(matched[3]) === lastDay
    },
    /** `yyyy-MM-dd` を読みやすく。空は「記録されていません」。 */
    dayLabel (value) {
      const matched = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (!matched) { return '記録されていません' }
      return matched[1] + '年' + Number(matched[2]) + '月'
        + Number(matched[3]) + '日'
    },
    /** 分を「◯時間」に。読めなければそのまま「—」。 */
    hoursLabel (minutes) {
      const hours = this.hoursOf(minutes)
      return hours === '' ? '—' : hours + '時間'
    },
    /** 分を時間へ。空は空のまま返す（空欄を既定値で埋め戻さない）。 */
    hoursOf (minutes) {
      if (minutes == null || minutes === '') { return '' }
      const value = Number(minutes)
      if (!Number.isFinite(value)) { return '' }
      // 分が時間で割り切れないときも、打ち直さずに済むよう小数で見せる。
      return String(Math.round((value / 60) * 100) / 100)
    },
    /** 時間を分へ。空は null（サーバが法定の原則値へ寄せる）。 */
    minutesOf (hours) {
      const text = hours == null ? '' : String(hours).trim()
      if (text === '') { return null }
      const value = Number(text)
      if (!Number.isFinite(value)) { return null }
      return Math.round(value * 60)
    },
    /**
     * 戻る。**協定の打ちかけも、捨てる前に聞く。**
     *
     * 協定を別の入れ物へ移したとき、ここを直し忘れると、協定だけ
     * 打ちかけの内容が黙って消える。消えたことは何も出ない。
     */
    backToSetting () {
      const changed = this.original &&
        (JSON.stringify(this.form) !== JSON.stringify(this.original) ||
          this.agreementChanged())
      if (!changed || window.confirm('入力内容が破棄されます。自社設定へ戻りますか？')) {
        this.$router.push({ name: '設定' })
      }
    },
    /** 協定の欄を触ったか。読み込んだときの姿と比べる。 */
    agreementChanged () {
      return JSON.stringify(this.agreement) !==
        JSON.stringify(this.agreementOriginal)
    },
    handleError (error, fallback) {
      const detail = error && error.response && error.response.data &&
        (error.response.data.message)
      this.errorMessage = detail || fallback
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
}
</script>

<style scoped>
.tenant-setting-edit-page { min-width: 0; padding: 24px; }
.page-header { margin-bottom: 24px; }
.page-header h1 { margin: 0 0 8px; font-weight: 800; }
.page-header p { max-width: 760px; margin: 0; color: #607884; line-height: 1.7; }
.eyebrow { margin: 0 0 4px; color: #0a6ca8; font-size: 12px; font-weight: 800; letter-spacing: .14em; }
.setting-loading { display: flex; align-items: center; justify-content: center; gap: 12px; min-height: 240px; color: #607884; }
.setting-form-card { min-width: 0; margin-bottom: 18px; }
.section-heading { display: flex; align-items: flex-start; gap: 12px; }
.section-heading strong { color: #17384d; font-size: 16px; }
.section-heading p { margin: 3px 0 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; }
.section-number { display: inline-flex; width: 34px; height: 34px; flex: 0 0 auto; align-items: center; justify-content: center; border-radius: 11px; background: #0a6ca8; color: #fff; font-weight: 800; }
label { color: #314f62; font-size: 12px; font-weight: 800; }
small { display: block; margin-top: -8px; margin-bottom: 10px; color: #778b97; line-height: 1.5; }
.error { min-height: 18px; margin: -8px 0 6px; color: #c93442; font-size: 12px; }
.asterisk { margin-left: 3px; color: #d33d4c; }
.switch-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-top: 8px; }
/* 36協定は別の保存。番号ではなく「36」を出して、上の連番と混ぜない。 */
.section-number--agreement { border-radius: 11px; background: #7a4bc4; font-size: 13px; letter-spacing: -.02em; }
.agreement-form { margin-top: 4px; }
/*
 * 保存の切れ目。**同じ画面に保存ボタンが2つあることを、見えるようにする。**
 * 切れ目が無いと、上の「確認」を押せば協定も保存されると読まれる。
 */
.separate-save-divider { display: flex; align-items: center; gap: 12px; margin: 30px 0 18px; color: #6a7f8c; font-size: 12px; font-weight: 800; }
.separate-save-divider::before,
.separate-save-divider::after { flex: 1 1 auto; height: 1px; background: #d7e3ea; content: ''; }
.separate-save-divider span { flex: 0 1 auto; }
/*
 * いま判定できているか。**7つの状態が、どれも同じ見た目にならないように。**
 * 色（--tone）で大きく分け、状態そのもの（--is-*）で左の線と札を変える。
 * 同じ強さなら後の規則が勝つので、状態のほうを後ろへ置く。
 */
.agreement-status { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 14px; padding: 14px; border: 1px solid #dce8ef; border-left: 6px solid #9fb4c1; border-radius: 12px; background: #f8fbfd; }
.agreement-status__badge { flex: 0 0 auto; padding: 4px 10px; border-radius: 999px; background: #9fb4c1; color: #fff; font-size: 12px; font-weight: 800; white-space: nowrap; }
.agreement-status__body { display: grid; min-width: 0; gap: 4px; }
.agreement-status__body strong { color: #17384d; font-size: 14px; }
.agreement-status__body small { margin: 0; color: #56707f; font-size: 12px; line-height: 1.7; }
.agreement-status__action { font-weight: 700; }
.agreement-status--ok { border-color: #bfe0c6; border-left-color: #2f8a4e; background: #f4fbf6; }
.agreement-status--ok .agreement-status__badge { background: #2f8a4e; }
.agreement-status--warning { border-color: #e8d3a6; border-left-color: #c07f12; background: #fffaf0; }
.agreement-status--warning .agreement-status__badge { background: #c07f12; }
.agreement-status--danger { border-color: #efc4c9; border-left-color: #c93442; background: #fdf5f6; }
.agreement-status--danger .agreement-status__badge { background: #c93442; }
.agreement-status--blocked { border-color: #d3ccea; border-left-color: #6a4bb0; background: #f8f6fd; }
.agreement-status--blocked .agreement-status__badge { background: #6a4bb0; }
.agreement-status--unknown { border-color: #cfd8de; border-left-color: #5b6b76; background: #f6f8f9; }
.agreement-status--unknown .agreement-status__badge { background: #5b6b76; }
/*
  この協定の位置づけ（新しい対象期間／改定）。**選ばせるところなので目立たせる。**
  ここを取り違えると、それまでに積み上がった時間が年間の上限から外れる。
*/
.agreement-period-mode {
  border: 1px solid #d7dee4;
  border-radius: 10px;
  margin: 0 0 16px;
  padding: 12px 16px 8px;
}
.agreement-period-mode legend {
  font-size: 13px;
  font-weight: 700;
  padding: 0 6px;
  width: auto;
}
.agreement-note {
  background: #fdf9f0;
  border-left: 4px solid #d9a441;
  color: #5c4a12;
  font-size: 12px;
  line-height: 1.8;
  margin: 8px 0 0;
  padding: 8px 12px;
}
/* 判定の対象ではない月（入社前）。**「判定できています」と同じ色にしない。** */
.agreement-status--outside { border-color: #d5d9dd; border-left-color: #8c959c; background: #fafbfc; }
.agreement-status--outside .agreement-status__badge { background: #8c959c; }
/* 状態ごとの見分け。**色が同じ組でも、線の太さと札の縁で別の顔にする。** */
.agreement-status--is-valid { border-left-width: 6px; }
.agreement-status--is-no_agreement { border-left-style: solid; border-left-width: 10px; }
.agreement-status--is-expired { border-left-style: double; border-left-width: 10px; }
.agreement-status--is-unconfirmed_system_default { border-left-style: dashed; border-left-width: 8px; }
.agreement-status--is-unsupported_non_month_start { border-left-style: solid; border-left-width: 10px; }
.agreement-status--is-overlapping_agreements { border-left-style: double; border-left-width: 10px; }
.agreement-status--is-insufficient_worktime_data { border-left-style: dotted; border-left-width: 10px; }
.agreement-status--is-unknown { border-left-style: dashed; border-left-width: 6px; }
/* いま「作る」のか「編集する」のか。**混ぜない。** */
.agreement-mode { display: flex; align-items: center; justify-content: space-between; gap: 14px; margin: 4px 0 16px; padding: 14px; border: 1px solid #dce8ef; border-radius: 12px; background: #f8fbfd; }
.agreement-mode div { display: grid; min-width: 0; gap: 3px; }
.agreement-mode strong { color: #17384d; font-size: 14px; }
.agreement-mode small { margin: 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; }
.agreement-mode--create { border-color: #cfe1ef; background: #f6fbff; }
.agreement-mode--edit { border-color: #e0d6f2; background: #faf7ff; }
/* 登録されている協定。**表示するだけにしない。編集の入口を置く。** */
.agreement-history { margin-bottom: 16px; overflow-x: auto; }
.agreement-history h6 { margin: 0 0 8px; color: #17384d; font-size: 13px; font-weight: 800; }
.agreement-history table { width: 100%; min-width: 720px; border-collapse: collapse; font-size: 13px; }
.agreement-history th { padding: 8px 10px; border-bottom: 2px solid #dfe8ee; color: #617887; font-size: 12px; font-weight: 800; text-align: left; white-space: nowrap; }
.agreement-history td { padding: 8px 10px; border-bottom: 1px solid #edf2f5; color: #17384d; white-space: nowrap; }
.agreement-history tr.is-editing td { background: #faf7ff; }
.agreement-history .badge { margin-left: 6px; }
.agreement-history__action-head { text-align: right; }
/* 内容が未登録の行。**編集の入口を出さない。**ここへ内容を入れて確定すると、
   ずっと前から効いている形のまま、過去の全部の月がその上限で判定される。 */
.agreement-history__placeholder { display: inline-block; max-width: 260px; color: #6a7f8c; font-size: 12px; line-height: 1.6; white-space: normal; }
/* サーバが導いた値。**入力欄と見た目を変えて、打つ場所ではないと分かるように。** */
.agreement-readonly { margin: 0 0 10px; padding: 9px 12px; border: 1px dashed #cfd8de; border-radius: 8px; background: #f4f6f8; color: #17384d; font-size: 14px; }
/* 月の途中で始まる（終わる）協定。**黙って丸めず、その場で言う。** */
.agreement-period-warning { display: grid; gap: 4px; margin: 0 0 14px; padding: 12px; border: 1px solid #d3ccea; border-left: 6px solid #6a4bb0; border-radius: 10px; background: #f8f6fd; }
.agreement-period-warning strong { color: #4a3480; font-size: 13px; }
.agreement-period-warning small { margin: 0; color: #55467f; font-size: 12px; line-height: 1.7; }
.agreement-error { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.agreement-actions { display: flex; justify-content: flex-end; margin-top: 18px; }
.agreement-actions .btn { min-width: 220px; min-height: 44px; }
.setting-switch-card,
.clear-token-option { display: flex; align-items: flex-start; gap: 10px; min-width: 0; padding: 14px; border: 1px solid #dce8ef; border-radius: 12px; background: #f8fbfd; cursor: pointer; }
.setting-switch-card input,
.clear-token-option input { width: 18px; height: 18px; flex: 0 0 auto; margin-top: 2px; }
.setting-switch-card span,
.clear-token-option span { display: grid; min-width: 0; gap: 3px; }
.setting-switch-card small,
.clear-token-option small { margin: 0; font-weight: 400; }
.slack-credential-card,
.notification-setting-card { min-width: 0; padding: 16px; border: 1px solid #dce8ef; border-radius: 14px; background: #fff; }
.slack-credential-card { margin-bottom: 14px; }
.is-disabled { background: #f4f6f8; opacity: .72; }
.credential-heading,
.notification-toggle-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 14px; }
.credential-heading h3,
.notification-toggle-row h3 { margin: 0 0 5px; color: #17384d; font-size: 15px; }
.credential-heading p,
.notification-toggle-row p { margin: 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; }
.notification-setting-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
.toggle-switch { display: flex; flex: 0 0 auto; align-items: center; gap: 7px; cursor: pointer; }
.toggle-switch input { position: absolute; opacity: 0; }
.toggle-switch span { position: relative; width: 42px; height: 24px; border-radius: 999px; background: #bdc9d0; transition: .18s ease; }
.toggle-switch span::after { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 2px 5px rgba(17, 49, 69, .2); content: ''; transition: .18s ease; }
.toggle-switch input:checked + span { background: #0a7abf; }
.toggle-switch input:checked + span::after { transform: translateX(18px); }
.toggle-switch input:disabled + span { cursor: not-allowed; opacity: .6; }
.toggle-switch strong { min-width: 28px; font-size: 12px; }
/* 入力欄のすぐ下に置く一言。**空欄が何を意味するか**を書く場所。 */
.field-hint { margin: 4px 0 10px; color: #6a7f8c; font-size: 12px; line-height: 1.7; }
/*
 * 「届いていません」の帯。**エラーの赤にしない。**画面は正しく動いていて、
 * 直すのは設定のほう。赤にすると壊れていると読まれ、読み飛ばされる。
 */
.delivery-notice { display: grid; gap: 4px; margin-bottom: 14px; padding: 12px; border: 1px solid #e8d3a6; border-radius: 10px; background: #fffaf0; }
.delivery-notice strong { color: #7c5510; font-size: 13px; }
.delivery-notice small { color: #7f6a49; font-size: 12px; line-height: 1.7; }
/* 届いてはいるが、届く場所が違う。**注意ではなく案内。** */
.delivery-notice--info { border-color: #cfe1ef; background: #f6fbff; }
.delivery-notice--info strong { color: #1d4a6c; }
.delivery-notice--info small { color: #4a6b81; }
.notification-schedule { display: grid; gap: 4px; margin-top: 12px; padding: 12px; border: 1px solid #dce8ef; border-radius: 10px; background: #f8fbfd; }
.notification-schedule strong { color: #314f62; font-size: 12px; }
.notification-schedule span { color: #17384d; font-size: 13px; font-weight: 800; }
.notification-schedule small { margin: 0; }
.form-actions { display: flex; justify-content: space-between; gap: 12px; margin-top: 24px; }
.form-actions .btn { min-width: 140px; min-height: 44px; }

@media (max-width: 1199.98px) {
  .notification-setting-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 767.98px) {
  .tenant-setting-edit-page { padding: 12px 0; }
  .page-header h1 { font-size: 28px; line-height: 1.25; }
  .switch-grid,
  .notification-setting-grid { grid-template-columns: minmax(0, 1fr); }
  .credential-heading,
  .notification-toggle-row { flex-wrap: wrap; }
  .toggle-switch { width: 100%; justify-content: flex-end; }
  .form-actions { flex-direction: column-reverse; }
  .form-actions .btn { width: 100%; }
  /* 帯と操作は縦に。**札や文言を切らない。** */
  .agreement-status { flex-direction: column; }
  .agreement-mode { flex-wrap: wrap; }
  .agreement-mode .btn { width: 100%; }
  .agreement-error { flex-direction: column; }
  .agreement-actions .btn { width: 100%; min-width: 0; }
}
</style>
